import React from 'react';
import styled from 'styled-components';
import CheckIconAlt from '@src/svgs/CheckIconAlt';

export interface Speaker {
  speakerLabel: string;
  name?: string | null;
  title?: string | null;
  bio?: string | null;
  voice?: string | null;
  isAnonymous: boolean;
}

interface SpeakerSelectorProps {
  speakers: Speaker[];
  selectedSpeakers: Speaker[];
  onChange: (selectedSpeakers: Speaker[]) => void;
}

const SpeakerSelector: React.FC<SpeakerSelectorProps> = ({
  speakers,
  selectedSpeakers,
  onChange
}) => {
  const toggleSpeaker = (speakerLabel: string) => {
    const selectedSpeakerLabels = selectedSpeakers.map(speaker => speaker.speakerLabel);
    if (selectedSpeakerLabels.includes(speakerLabel)) {
      onChange(selectedSpeakers.filter(speaker => speaker.speakerLabel !== speakerLabel));
    } else {
      onChange([...selectedSpeakers, speakers.find(s => s.speakerLabel === speakerLabel)!]);
    }
  };
  
  const selectAll = () => {
    onChange(speakers);
  };
  
  const deselectAll = () => {
    onChange([]);
  };
  
  return (
    <SpeakerSelectorContainer>
      <SpeakerSelectorHeader>
        <SpeakerSelectorTitle>Filter by Speaker</SpeakerSelectorTitle>
        <SpeakerSelectorActions>
          <ActionButton onClick={selectAll}>Select All</ActionButton>
          <ActionButton onClick={deselectAll}>Deselect All</ActionButton>
        </SpeakerSelectorActions>
      </SpeakerSelectorHeader>
      
      <SpeakerList>
        {speakers.map(speaker => {
          // Check if speaker is selected by comparing speakerLabel
          const isSelected = selectedSpeakers.some(s => s.speakerLabel === speaker.speakerLabel);
          
          return (
            <SpeakerItem key={speaker.speakerLabel}>
              <CheckBox 
                isChecked={isSelected}
                onClick={() => toggleSpeaker(speaker.speakerLabel)}
              >
                {isSelected && <CheckIconAlt />}
              </CheckBox>
              <SpeakerInfo>
                {speaker.isAnonymous ? (
                  <SpeakerName>Speaker {speaker.speakerLabel.replace('spk_', '')}</SpeakerName>
                ) : (
                  <>
                    <SpeakerName>{speaker.name}</SpeakerName>
                    {speaker.title && <SpeakerTitle>{speaker.title}</SpeakerTitle>}
                  </>
                )}
              </SpeakerInfo>
            </SpeakerItem>
          );
        })}
      </SpeakerList>
    </SpeakerSelectorContainer>
  );
};

// Styled components
const SpeakerSelectorContainer = styled.div`
  margin-top: 12px;
  background: #1a1a2a;
  border-radius: 4px;
  padding: 12px;
`;

const SpeakerSelectorHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin-bottom: 12px;
  gap: 8px;
`;

const SpeakerSelectorTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #f2d093;
`;

const SpeakerSelectorActions = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background-color: transparent;
  color: #f3e9d7;
  border: 1px solid #484848;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(242, 208, 147, 0.1);
  }
`;

const SpeakerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
`;

const SpeakerItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.05);
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const CheckBox = styled.div<{ isChecked: boolean }>`
  border: 0;
  outline: 0;
  background-color: ${(props) => (props.isChecked ? '#4AD067' : 'transparent')};
  width: 16px;
  height: 16px;
  border-radius: 2px;
  overflow: hidden;
  border: ${(props) => !props.isChecked && '1px solid #9B9B9B'};
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const SpeakerInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const SpeakerName = styled.span`
  font-weight: 500;
  font-size: 14px;
  color: #f3e9d7;
`;

const SpeakerTitle = styled.span`
  font-size: 12px;
  color: #aaa;
`;

export default SpeakerSelector;